/**
 * Is this event coming from somewhere the browser's own text editing belongs?
 *
 * Every document-level keyboard shortcut in this app has to ask it before acting, so the question
 * has one answer. Promoted out of `pages/plan-builder/lib/usePlanShortcuts` on 18 September 2026 on
 * its second consumer, `pages/tasks/lib/useTaskShortcuts` — where a bare `→` would otherwise mark a
 * task done while the trainer was moving the caret through a session note.
 *
 * **An `<input>` is not automatically one.** Every `HTMLInputElement` used to answer yes, and the
 * builder paints half its controls as clipped radios behind a label — the category chips, the cover
 * grid, the Build/Deload pair. Focus sat on one of them the moment the trainer used it, so Cmd+Z
 * did nothing on the controls it is most needed after, while the browser had no typing to undo
 * there either. A radio holds no editable value; there is nothing to defer to.
 */

/**
 * The `<input>` types that hold no text, and therefore have no undo of their own to defer to.
 *
 * An EXCLUSION list rather than an allow-list of text types, so a type this build has not heard of
 * keeps the browser's behaviour: getting that wrong in the safe direction costs a trainer one
 * keystroke, and getting it wrong in the other loses what they were typing. `input.type` normalises
 * anything unrecognised to `text`, so the list is closed in practice.
 */
const VALUELESS_INPUT_TYPES: ReadonlySet<string> = new Set([
  'button',
  'checkbox',
  'color',
  'file',
  'hidden',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
]);

export function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLTextAreaElement) return true;
  return target instanceof HTMLInputElement && !VALUELESS_INPUT_TYPES.has(target.type);
}
